import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * PaperCard — a "lifted paper" surface for hero/featured content.
 *
 * White card on warm linen with a soft layered shadow that reads like
 * a printed page laid on a table. Reserve for content that earns the
 * spotlight: persona profiles, walkthrough reports, scenario briefs.
 *
 * Not for list rows — the lift is a featured-content treatment. Use
 * `Tonal` for quieter editorial asides and the bare list chrome for
 * scannable rows.
 *
 * Padding scale:
 *   - sm: p-6                 (compact featured blocks)
 *   - md: p-8 / sm:p-10       (default — section-level features)
 *   - lg: p-10 / sm:p-12      (hero — persona/profile cards)
 *   - xl: p-12 / sm:p-16      (page hero — report containers)
 */

const paddingStyles = {
  sm: "p-6",
  md: "p-8 sm:p-10",
  lg: "p-10 sm:p-12",
  xl: "p-12 sm:p-16",
} as const;

type PaperCardElement = "div" | "article" | "section" | "li";

interface PaperCardProps extends React.HTMLAttributes<HTMLElement> {
  padding?: "sm" | "md" | "lg" | "xl";
  /** Render as a different semantic element. Defaults to <div>. */
  as?: PaperCardElement;
}

export function PaperCard({
  className,
  padding = "md",
  as: Tag = "div",
  ...props
}: PaperCardProps) {
  return (
    <Tag
      data-slot="paper-card"
      className={cn(
        "bg-card border border-border/70 dark:border-border",
        // Sharp close-contact shadow + mid lift + subtle far cast.
        // In dark mode: specular top-edge highlight bevel and deep atmospheric drop shadow.
        "shadow-[0_3px_8px_rgba(40,28,16,0.18),0_16px_32px_-8px_rgba(40,28,16,0.10),0_48px_80px_-24px_rgba(40,28,16,0.06)] dark:shadow-[inset_0_1px_0_0_oklch(1_0_0_/_0.08),0_8px_32px_-8px_oklch(0_0_0_/_0.55)]",
        paddingStyles[padding],
        className,
      )}
      {...props}
    />
  );
}
